// ============================================================
// ClientsPage.js
// Global list of all Clients across all Tenants. Filters: Tenant
// dropdown + search.
//
// URL state:
//   ?page=N, ?pageSize=N, ?tenantId=N, ?search=...
//
// Row click → /clients/:clientId (detail page — ships in a later
// session). For now, rows are visually clickable but the
// destination route renders ComingSoonPage.
// ============================================================

import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, X } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import { LifecycleStatusBadge } from '../components/StatusBadge';
import Badge from '../components/Badge';
import CreateClientDialog from './CreateClientDialog';
import { listAdminClients } from '../api/adminClients';
import { listTenants } from '../api/tenants';
import { translateError } from '../utils/errorMessages';
import { formatDate } from '../utils/format';
import './TenantsPage.css';
import './ClientsPage.css';

const DEFAULT_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const CLIENT_TYPE_LABEL = {
  care_home:          'Care home',
  domiciliary_agency: 'Domiciliary agency',
  mixed:              'Mixed',
};

function parsePageParam(raw, fallback) {
  if (raw == null) return fallback;
  const n = parseInt(raw, 10);
  return Number.isInteger(n) && n > 0 ? n : fallback;
}

export default function ClientsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page     = parsePageParam(searchParams.get('page'), 1);
  const pageSize = parsePageParam(searchParams.get('pageSize'), DEFAULT_PAGE_SIZE);
  const urlSearch  = searchParams.get('search') || '';
  const urlTenant  = searchParams.get('tenantId') || '';

  const [searchInput, setSearchInput] = useState(urlSearch);
  const debounceRef = useRef(null);

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [tenants, setTenants] = useState([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  useEffect(() => { setSearchInput(urlSearch); }, [urlSearch]);

  // Tenant dropdown — independent fetch; fail-soft if it breaks.
  useEffect(() => {
    let cancelled = false;
    listTenants({ pageSize: 200 })
      .then((res) => { if (!cancelled) setTenants(res.items || []); })
      .catch(() => { /* falls back to "All tenants" only */ });
    return () => { cancelled = true; };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, pageSize };
      if (urlSearch) params.search = urlSearch;
      if (urlTenant) params.tenantId = parseInt(urlTenant, 10);
      const res = await listAdminClients(params);
      setItems(res.items || []);
      setTotalCount(res.totalCount || 0);
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, urlSearch, urlTenant]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleSearchChange(value) {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set('search', value); else next.delete('search');
        next.delete('page');
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
  }

  function handleTenantChange(value) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set('tenantId', value); else next.delete('tenantId');
      next.delete('page');
      return next;
    });
  }

  function handlePageChange(nextPage) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextPage === 1) next.delete('page'); else next.set('page', String(nextPage));
      return next;
    });
  }

  function clearSearch() {
    setSearchInput('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('search');
      next.delete('page');
      return next;
    });
  }

  const columns = [
    {
      key: 'clientName',
      label: 'Client Name',
      render: (row) => <span className="tenants-page__name">{row.clientName}</span>,
    },
    {
      key: 'clientType',
      label: 'Type',
      render: (row) => (
        <Badge variant="neutral">{CLIENT_TYPE_LABEL[row.clientType] || row.clientType}</Badge>
      ),
      width: '160px',
    },
    {
      key: 'tenant',
      label: 'Tenant',
      mobile: 'subtitle',
      render: (row) => (
        <span className={row.tenantName ? 'tenants-page__name' : 'tenants-page__muted'}>
          {row.tenantName || '—'}
        </span>
      ),
      width: '180px',
    },
    {
      key: 'lifecycle',
      label: 'Lifecycle',
      mobile: 'badge',
      render: (row) => <LifecycleStatusBadge status={row.lifecycleStatus} />,
      width: '110px',
    },
    {
      key: 'userCount',
      label: 'Users',
      render: (row) => <span className="tenants-page__count">{row.userCount}</span>,
      width: '90px',
      align: 'right',
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (row) => <span className="tenants-page__muted">{formatDate(row.createdAt)}</span>,
      width: '140px',
    },
  ];

  function handleRowClick(row) {
    navigate(`/clients/${row.clientId}`);
  }

  function handleClientCreated() {
    fetchData();
  }

  const hasNoResults = !loading && !error && items.length === 0;
  const isFiltered = Boolean(urlSearch || urlTenant);

  // defaultTenantId for the create dialog: prefer the current
  // tenantId filter (operator likely wants to add a client to the
  // tenant they're already viewing).
  const defaultTenantId = urlTenant ? parseInt(urlTenant, 10) : undefined;

  return (
    <>
      <PageHeader
        title="Clients"
        subtitle="Care organisations using CareHub."
        cta={
          <button
            type="button"
            className="tenants-page__create"
            onClick={() => setIsCreateOpen(true)}
          >
            <Plus size={16} />
            Create Client
          </button>
        }
      />

      <div className="clients-page__toolbar">
        <div className="clients-page__filters">
          <label className="clients-page__tenant-filter">
            <span className="clients-page__tenant-label">Tenant</span>
            <select
              value={urlTenant}
              onChange={(e) => handleTenantChange(e.target.value)}
              className="clients-page__tenant-select"
            >
              <option value="">All tenants</option>
              {tenants.map((t) => (
                <option key={t.tenantId} value={String(t.tenantId)}>{t.tenantName}</option>
              ))}
            </select>
          </label>

          <div className="tenants-page__search">
            <Search size={16} className="tenants-page__search-icon" />
            <input
              type="search"
              placeholder="Search clients…"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="tenants-page__search-input"
            />
            {searchInput && (
              <button
                type="button"
                className="tenants-page__search-clear"
                onClick={clearSearch}
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      </div>

      <DataTable
        data={items}
        columns={columns}
        rowKey={(row) => row.clientId}
        onRowClick={handleRowClick}
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        loading={loading}
        error={error}
        onRetry={fetchData}
        emptyMessage={isFiltered ? 'No clients match your filters.' : 'No Clients yet.'}
        emptyAction={
          !isFiltered && hasNoResults ? (
            <button
              type="button"
              className="tenants-page__create-empty"
              onClick={() => setIsCreateOpen(true)}
            >
              <Plus size={16} />
              Create your first Client
            </button>
          ) : null
        }
      />

      <CreateClientDialog
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={handleClientCreated}
        defaultTenantId={defaultTenantId}
      />
    </>
  );
}
